import React, { FunctionComponent, memo, useRef, useEffect, useState, RefObject } from "react";
import { Box } from "@chakra-ui/react";
import assert from "assert";
import splitEvery from "ramda/src/splitEvery";
import {
  curveBasis,
  line,
  easeLinear,
  select,
  Selection,
} from "d3";
import { throttle, isEqual } from "lodash";

import {
  SiteConfig_siteConfig_dashboardConf_components,
  SiteConfig_siteConfig_dashboardConf_connections,
} from "graphql/generated";

import { useGlobalState } from "common/global-state";


class Vector2D {
  constructor(public readonly x: number, public readonly y: number) {}

  private squaredDist(other: Vector2D): number {
    return (this.x - other.x) ** 2 + (this.y - other.y) ** 2;
  }

  dist(other: Vector2D): number {
    return Math.sqrt(this.squaredDist(other));
  }
}

class GraphNode {
  public readonly connections: Set<GraphNode> = new Set();

  constructor(public readonly coords: Vector2D) {}
}

class Graph {
  public readonly nodes: Array<GraphNode> = [];

  registerNode(coords: Vector2D): GraphNode {
    const node = new GraphNode(coords);

    this.nodes.push(node);

    return node;
  }

  connect(a: GraphNode, b: GraphNode): Graph {
    assert(this.nodes.indexOf(a) >= 0);
    assert(this.nodes.indexOf(b) >= 0);

    a.connections.add(b);
    b.connections.add(a);

    return this;
  }

  getPath(start: GraphNode, target: GraphNode): Array<GraphNode> {
    assert(this.nodes.indexOf(start) >= 0);
    assert(this.nodes.indexOf(target) >= 0);

    const openSet = new Set<GraphNode>([start]);

    const parents = new Map<GraphNode, GraphNode>();
    const gScore = new Map<GraphNode, number>();
    const fScore = new Map<GraphNode, number>();

    for (const node of this.nodes) {
      gScore.set(node, Infinity);
      fScore.set(node, Infinity);
    }

    gScore.set(start, 0);
    fScore.set(start, start.coords.dist(target.coords));

    while (openSet.size > 0) {
      const aspirants: Array<[GraphNode, number]> = Array.from(
        openSet.values()
      ).map((x) => [x, fScore.get(x) ?? Infinity]);
      const [current] = aspirants.reduce((acc, x) => (x[1] < acc[1] ? x : acc));

      if (current === target) {
        let pathItem = current;
        const resultPath: Array<GraphNode> = [current];

        while (parents.has(pathItem)) {
          pathItem = parents.get(pathItem) as GraphNode;
          resultPath.push(pathItem);
        }

        return resultPath;
      }

      openSet.delete(current);

      current.connections.forEach((neighbor) => {
        const tentativeGScore = (gScore.get(current) ?? 0) + current.coords.dist(neighbor.coords);

        if (tentativeGScore < (gScore.get(neighbor) ?? Infinity)) {
          parents.set(neighbor, current);
          gScore.set(neighbor, tentativeGScore);
          fScore.set(
            neighbor,
            tentativeGScore + neighbor.coords.dist(target.coords)
          );
          openSet.add(neighbor);
        }
      });
    }

    return [];
  }
}


interface IGridCard {
  topAnchor: GraphNode;
  bottomAnchor: GraphNode;
  leftAnchor: GraphNode;
  rightAnchor: GraphNode;
}

interface IConnection extends SiteConfig_siteConfig_dashboardConf_connections {
  index: number;
  flow: string;
  start: number;
  end: number;
  duration: number;
}

interface IFlows {
  [key: string]: string;
}

interface IWindowSize {
  width: number;
  height: number;
}

interface IProps {
  containerRef: RefObject<HTMLDivElement>;
  gridWidth: number;
  gridHeight: number;
  components: Array<SiteConfig_siteConfig_dashboardConf_components>;
  connections: Array<SiteConfig_siteConfig_dashboardConf_connections>;
  flows?: IFlows;
}


const isFlowing = (flow: string): boolean => {
  return /.-to-./.test(flow);
}


const Flows: FunctionComponent<IProps> = memo(function Flows({
  containerRef,
  gridWidth,
  gridHeight,
  components,
  connections,
  flows = {},
}) {
  const { connected } = useGlobalState();
  const [windowSize, setWindowSize] = useState<IWindowSize>({ width: 0, height: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const flowsRef = useRef<Selection<any, any, any, any>>();
  const flowsDataRef = useRef<IFlows>(flows);
  const graphRef = useRef<Graph>(new Graph());
  const gridCardsRef = useRef<Array<Array<IGridCard>>>([]);

  useEffect(() => {
    if (flows && Object.keys(flows).length > 0) {
      flowsDataRef.current = flows;
      flowsRef.current?.each(function(d: IConnection) {
        d.flow = flows[d.id] || d.defaultFlow;
      });
    }
  }, [flows]);

  // Calculate the coordinates of cards and connection end points
  //
  useEffect(() => {
    const containerEl = containerRef.current;
    assert(containerEl);

    const graph = new Graph();
    const containerBr = containerEl.getBoundingClientRect();
    const cards = Array.from(containerEl.children).map((x) => {
      const itemBr = x.getBoundingClientRect();

      const x0 = itemBr.x - containerBr.x;
      const y0 = itemBr.y - containerBr.y;

      return {
        topAnchor: graph.registerNode(
          new Vector2D(x0 + itemBr.width / 2, y0)
        ),
        bottomAnchor: graph.registerNode(
          new Vector2D(x0 + itemBr.width / 2, y0 + itemBr.height)
        ),
        leftAnchor: graph.registerNode(
          new Vector2D(x0, y0 + itemBr.height / 2)
        ),
        rightAnchor: graph.registerNode(
          new Vector2D(x0 + itemBr.width, y0 + itemBr.height / 2)
        ),
      };
    });

    const gridCards: Array<Array<IGridCard>> = splitEvery(gridWidth, cards);
    const midNodes: Array<Array<GraphNode>> = [];

    for (let y = 0; y < gridHeight; y++) {
      const midNodesRow: Array<GraphNode> = [];

      midNodes.push(midNodesRow);

      for (let x = 0; x < gridWidth; x++) {
        let rightIntermediateNode = null;
        let bottomIntermediateNode = null;

        if (x < gridWidth - 1) {
          const currNode = gridCards[y][x].rightAnchor;
          const neighborNode = gridCards[y][x + 1].leftAnchor;
          const gapSize = neighborNode.coords.x - currNode.coords.x;

          rightIntermediateNode = graph.registerNode(
            new Vector2D(currNode.coords.x + 0.5 * gapSize, currNode.coords.y)
          );

          graph.connect(rightIntermediateNode, currNode);
          graph.connect(rightIntermediateNode, neighborNode);

          if (y > 0) {
            graph.connect(rightIntermediateNode, midNodes[y - 1][x]);
          }
        }

        if (y < gridHeight - 1) {
          const currNode = gridCards[y][x].bottomAnchor;
          const neighborNode = gridCards[y + 1][x].topAnchor;
          const gapSize = neighborNode.coords.y - currNode.coords.y;

          bottomIntermediateNode = graph.registerNode(
            new Vector2D(currNode.coords.x, currNode.coords.y + 0.5 * gapSize)
          );

          graph.connect(bottomIntermediateNode, currNode);
          graph.connect(bottomIntermediateNode, neighborNode);

          if (x > 0) {
            graph.connect(bottomIntermediateNode, midNodesRow[x - 1]);
          }
        }

        if (rightIntermediateNode && bottomIntermediateNode) {
          const midNode = graph.registerNode(
            new Vector2D(
              rightIntermediateNode.coords.x,
              bottomIntermediateNode.coords.y
            )
          );

          midNodesRow.push(midNode);

          graph.connect(midNode, rightIntermediateNode);
          graph.connect(midNode, bottomIntermediateNode);
        }
      }
    }

    gridCardsRef.current = gridCards;
    graphRef.current = graph;
  }, [gridWidth, gridHeight, components, windowSize, containerRef]);


  // Create DOM paths per each connection and setup their shared properties.
  //
  useEffect(() => {
    const svgEl = select(svgRef.current);

    flowsRef.current = svgEl
      .selectAll(".dashboard-card-flow")
      .data( connections )
      .join("path")
        .attr("class", "dashboard-card-flow")
        ;
  }, [connections]);


  // Drive the flows animations.
  //
  useEffect(() => {
    const animSpeed: number = 28; // px/s
    const animTime: number = 2; // s
    const animTravel: number = animSpeed * animTime; // px
    const animLoopRefresh: number = 0.5;

    const isNodeAttached = (node: HTMLElement): boolean => {
      let parentNode: (Node & ParentNode) | null | null = node.parentNode;

      while (parentNode){
        parentNode = parentNode.parentNode;

        if (parentNode === document) return true;
        if (!parentNode) return false;
      }

      return false;
    }

    const dashAnimation = (index: number | null = null) => {
      assert(flowsRef.current);

      flowsRef.current
        .filter(function(_d, i: number) {
          const relevant: boolean = index === null || index === i;

          if (relevant && isNodeAttached(this)) return relevant;

          return false;
        })
        .each(function(d: IConnection, i: number) {
          d.index = index === null ? i : d.index;
          d.flow = (flowsDataRef.current && flowsDataRef.current[d.id]) || d.defaultFlow;

          if (connected && isFlowing(d.flow)) {
            d.end = (d.flow === "a-to-b" ? animTravel : -animTravel) / 2; // px

            const start: number = +select(this).attr("stroke-dashoffset"); // px
            const rest: number = Math.abs(d.end - start) / animTravel; // decimal 0-1

            d.start = rest > 0 ? start : -d.end; // px
            d.duration = rest > 0 ? rest * animTime : animTime; // s
          } else {
            d.start = d.end = 0; // px
            d.duration = animLoopRefresh; // s
          }
        })
        .attr("stroke-dashoffset", ({ start }: IConnection) => start)
        .transition("flows")
          .duration(({ duration }: IConnection) => duration * 1000)
          .ease(easeLinear)
          .attr("flow", ({ flow }: IConnection) => flow)
          .attr("stroke-dashoffset", ({ end }: IConnection) => end)
          .on("end", ({ index }: IConnection) => dashAnimation(index))
        ;
    }

    dashAnimation();

    return () => {
      flowsRef.current?.interrupt("flows");
    };
  }, [connections, connected]);


  // Draw each connection link curve in a direction according to connection's flow
  //
  useEffect(() => {
    assert(flowsRef.current);

    const curve = line().curve(curveBasis);
    const edgePointToGraphNode = (id: string, anchor: string) => {
      const cardConfig = components.find((x) => x.cardId === id);
      assert(cardConfig, `Unable to find edge point "${id}"`);

      const gridCard = gridCardsRef.current[cardConfig.y][cardConfig.x];

      switch (anchor) {
        case "top":
          return gridCard.topAnchor;

        case "bottom":
          return gridCard.bottomAnchor;

        case "left":
          return gridCard.leftAnchor;

        case "right":
          return gridCard.rightAnchor;

        default:
          throw new Error(
            `Unexpected card connection anchor "${anchor}" for card with id "${cardConfig.cardId}"`
          );
      }
    };

    flowsRef.current
      .sort(({ flow }: IConnection) => isFlowing(flow) ? 1 : -1 )
      .attr("flow", ({ flow }: IConnection) => flow )
      .attr("connection", ({ id }: IConnection) => id )
      .attr("d", ({ componentA, componentB }: IConnection) => {
        const startNode = edgePointToGraphNode(componentA.id, componentA.anchor);
        const targetNode = edgePointToGraphNode(componentB.id, componentB.anchor);
        const path = graphRef.current.getPath(startNode, targetNode);

        return curve(path.map((node) => [node.coords.x, node.coords.y]));
      })
      ;
  }, [components, connections, windowSize]);


  // Resolve `windowSize` state and update it on window size change.
  //
  useEffect(() => {
    const containerEl = containerRef.current;
    assert(containerEl);

    const updateSize = () => {
      setWindowSize({
        width: containerEl.offsetWidth,
        height: containerEl.offsetHeight,
      });
    };
    updateSize();

    const throttledUpdateSize = throttle(updateSize, 10);
    const resizeObserver = new ResizeObserver(throttledUpdateSize);

    resizeObserver.observe(containerEl);

    return () => {
      resizeObserver.disconnect();
      throttledUpdateSize.cancel();
    }
  }, [containerRef]);

  return (
    <Box position="relative" p={5} h={windowSize.height} mb={-windowSize.height}>
      <svg
        className="flows-viz"
        width="100%"
        height="100%"
        viewBox={`0 0 ${windowSize.width} ${windowSize.height}`}
        preserveAspectRatio={"none"}
        ref={svgRef}
      />
    </Box>
  );
}, isEqual);

export default Flows;
